/*
 * /family — trang của bố/mẹ: xem tình hình học của con. Chỉ email được learner
 * khai báo ở /settings mới được xem (GET /api/family → authorized + learners[]).
 */
import { useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Page, PageHeader, UserAvatar, usePageTitle } from "@/components/page"
import { useSession } from "@/app/session"
import { api, loginUrl } from "@/lib/api"
import { formatDisplayDate } from "@/lib/format"

function CourseRow({ course }: { course: any }) {
  return (
    <li className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-line bg-paper p-4 sm:flex-row sm:items-center">
      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-soft">
        <Icon name="book-open" className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold tracking-[0.08em] text-orange uppercase">Khóa học</p>
        <h3 className="font-heading text-lg font-semibold text-ink">{course.course_name}</h3>
        <p className="text-sm text-muted-foreground">
          {course.next_session_date
            ? `Buổi tiếp theo: ${formatDisplayDate(course.next_session_date)}${course.next_session_time ? ` · ${course.next_session_time}` : ""}`
            : "Chưa có lịch học tiếp theo"}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="solid">{Number(course.total_points || 0).toLocaleString("vi-VN")} XP</Badge>
        <Badge>{course.badge_count || 0} badge</Badge>
      </div>
    </li>
  )
}

function LearnerCard({ learner }: { learner: any }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <UserAvatar user={{ full_name: learner.name, avatar_url: learner.avatar_url }} size="lg" className="size-14" />
          <div className="min-w-0">
            <p className="text-sm font-bold tracking-[0.08em] text-muted-foreground uppercase">
              {learner.relationship === "father" ? "Bố" : "Mẹ"} đang xem
            </p>
            <h2 className="truncate font-heading text-2xl font-semibold text-ink">{learner.name}</h2>
          </div>
        </div>
        {learner.classes?.length ? (
          <ul className="flex flex-col gap-3">
            {learner.classes.map((c: any) => (
              <CourseRow key={c.id} course={c} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Con chưa tham gia lớp nào.</p>
        )}
      </CardContent>
    </Card>
  )
}

export default function FamilyPage() {
  usePageTitle("Gia đình", "Bố mẹ theo dõi thông tin học tập của con trên Simba.")
  const { loading, user } = useSession()
  const [data, setData] = useState<any>(null)
  const [error, setError] = useState("")

  const load = () => {
    setError("")
    api("/api/family")
      .then(setData)
      .catch((e: any) => {
        if (e.status === 401) location.href = loginUrl(location.href)
        else setError(e.message)
      })
  }
  useEffect(() => {
    if (loading) return
    if (!user) {
      location.href = loginUrl(location.href)
      return
    }
    load()
  }, [loading, user?.id])

  if (loading || !user) return <LoadingState label="Đang kiểm tra quyền của bố mẹ…" />

  return (
    <Page className="max-w-[880px]">
      <PageHeader eyebrow="Gia đình" title="Thông tin học tập của con" description="Chỉ tài khoản có email được learner khai báo mới nhìn thấy trang này." />
      {error && <ErrorState message={error} onRetry={load} />}
      {!data && !error && <Skeleton className="h-56 rounded-[20px]" />}
      {data && !data.authorized && (
        <EmptyState
          mascot="meerkat-thinking"
          title="Chưa được chia sẻ"
          description="Email đăng nhập này chưa trùng với email bố hoặc mẹ mà learner đã khai báo."
        />
      )}
      {data?.authorized && (data.learners || []).map((l: any) => <LearnerCard key={l.id} learner={l} />)}
    </Page>
  )
}
